import { blueWhiteThemeV2 } from './blue-white';
import { darkThemeV2 } from './dark';
import {
  type AffineThemeKeyV2,
  type AffineThemeV2,
  lightThemeV2,
} from './light';

export { blueWhiteThemeV2, darkThemeV2, lightThemeV2 };

/** Every theme the app ships, by its `data-theme` name. */
export const themesV2 = {
  light: lightThemeV2,
  dark: darkThemeV2,
  'blue-white': blueWhiteThemeV2,
} as const satisfies Record<string, AffineThemeV2>;

export type ThemeName = keyof typeof themesV2;

export const THEME_NAMES = Object.keys(themesV2) as ThemeName[];

export function isThemeName(name: string | undefined): name is ThemeName {
  return name !== undefined && Object.hasOwn(themesV2, name);
}

/**
 * The theme the app shows for the chosen `theme` (a name or `system`) and the
 * `resolved` theme of next-themes. Before the first resolve it shows light.
 * The boot splash and embedded App pages follow this one rule.
 */
export function displayedThemeName(
  theme: string | undefined,
  resolved: string | undefined
): ThemeName {
  const name = theme === 'system' ? resolved : (theme ?? resolved);
  return isThemeName(name) ? name : 'light';
}
export type { AffineThemeKeyV2, AffineThemeV2 };

export function themeToVar(theme: AffineThemeKeyV2): string {
  return `--streamlain-v2-${theme.split('/').join('-')}`;
}

function toCssVariables(theme: AffineThemeV2): Record<string, string> {
  return Object.fromEntries(
    (Object.keys(theme) as AffineThemeKeyV2[]).map(role => [
      themeToVar(role),
      theme[role],
    ])
  );
}

export const lightCssVariablesV2 = toCssVariables(lightThemeV2);
export const darkCssVariablesV2 = toCssVariables(darkThemeV2);
export const blueWhiteCssVariablesV2 = toCssVariables(blueWhiteThemeV2);

type NestRole<K extends string> = K extends `${infer Group}/${infer Rest}`
  ? { readonly [P in Group]: NestRole<Rest> }
  : { readonly [P in K]: string };

type UnionToIntersection<U> = (
  U extends unknown ? (union: U) => void : never
) extends (intersection: infer I) => void
  ? I
  : never;

/** The roles as a property tree: `text/primary` becomes `text.primary`. */
export type NestedTheme = UnionToIntersection<NestRole<AffineThemeKeyV2>>;

function _cssVarV2(key: AffineThemeKeyV2, fallback?: string): string {
  return `var(${themeToVar(key)}${fallback ? `, ${fallback}` : ''})`;
}

const roles: Record<string, string> = lightThemeV2;

function nestedCssVar(group = ''): typeof _cssVarV2 {
  return new Proxy(_cssVarV2, {
    get(_target, property) {
      const role = group ? `${group}/${String(property)}` : String(property);
      return typeof roles[role] !== 'string'
        ? nestedCssVar(role)
        : _cssVarV2(role as AffineThemeKeyV2);
    },
    apply(_target, _this, [key, fallback]: [AffineThemeKeyV2, string?]) {
      return group
        ? _cssVarV2(group as AffineThemeKeyV2, key)
        : _cssVarV2(key, fallback);
    },
  });
}

/**
 * Type-safe css variable of a theme role (v2).
 *
 * ```ts
 * cssVarV2('text/primary');
 * cssVarV2.text.primary;
 * ```
 */
export const cssVarV2 = nestedCssVar() as typeof _cssVarV2 & NestedTheme;

/** How strongly a role tints a hint box or a status badge. */
export const TINT_SHARE = { hint: 8, badge: 16 } as const;

/**
 * A light wash of a role over whatever surface it sits on, for hint boxes and
 * status badges. The share is one of `TINT_SHARE`.
 */
export function tintV2(
  role: AffineThemeKeyV2,
  share: keyof typeof TINT_SHARE
): string {
  return `color-mix(in srgb, ${_cssVarV2(role)} ${TINT_SHARE[share]}%, transparent)`;
}
